import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PageHero } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/lib/api";
import {
  Search, LifeBuoy, Rocket, UserCircle, CalendarDays, CheckCircle2, Gift, Layers,
  GraduationCap, BookOpen, HeartPulse, BarChart3, Users, ShieldCheck, Briefcase, Image as ImageIcon,
  FileText, ListChecks, PoundSterling, CalendarClock, FolderOpen,
} from "lucide-react";

const ICONS = { Rocket, UserCircle, CalendarDays, CheckCircle2, Gift, Layers, GraduationCap, BookOpen, HeartPulse, BarChart3, Users, ShieldCheck, Briefcase, Image: ImageIcon, LifeBuoy, FileText, ListChecks, PoundSterling, CalendarClock, FolderOpen };
const Ic = ({ name, ...p }) => { const C = ICONS[name] || BookOpen; return <C {...p} />; };

export default function Help() {
  const loc = useLocation();
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState(() => new URLSearchParams(loc.search).get("q") || "");
  const [cat, setCat] = useState("All");
  const [openId, setOpenId] = useState(() => new URLSearchParams(loc.search).get("article") || "");
  useEffect(() => {
    const a = new URLSearchParams(loc.search).get("article") || "";
    setOpenId(a);
    if (a) setTimeout(() => document.getElementById(`article-${a}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
  }, [loc.search]);
  useEffect(() => { api.get("/help/articles").then((r) => setArticles(r.data.articles || [])).catch(() => setArticles([])); }, []);

  const cats = ["All", ...Array.from(new Set(articles.map((a) => a.category)))];
  const term = q.trim().toLowerCase();
  const filtered = articles.filter((a) => {
    if (cat !== "All" && a.category !== cat) return false;
    if (!term) return true;
    const hay = `${a.title} ${a.summary} ${a.category} ${(a.body || []).map((s) => `${s.h} ${s.p}`).join(" ")}`.toLowerCase();
    return hay.includes(term);
  });

  return (
    <div className="space-y-6" data-testid="help-page">
      <PageHero title="Help centre" subtitle="Guides and answers for every part of the CASS HR portal — tailored to your role." icon={LifeBuoy} eyebrow="Help & support" tone="teal" pageKey="help" />

      <div className="relative max-w-xl">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help — e.g. book leave, publish statement…"
          className="pl-9 bg-white" data-testid="help-search" aria-label="Search help articles" />
      </div>

      <div className="flex flex-wrap gap-2" data-testid="help-categories">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`help-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${cat === c ? "bg-[#0B1A2F] text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:border-[#2EA3FD]"}`}>
            {c}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400" data-testid="help-empty">
          <Search size={26} className="mx-auto mb-2 text-slate-300" />
          No help articles match "{q}". Try a different search or use the ? button on any page.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((a) => { const aid = a.key || a.id; return (
            <div key={a.id} id={`article-${aid}`} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`help-article-${aid}`}>
              <div className="px-5 pt-4 pb-1 flex items-start gap-3">
                <span className="rounded-lg bg-[#EFF8FF] text-[#2EA3FD] p-2 shrink-0"><Ic name={a.icon} size={18} /></span>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{a.category}</p>
                  <h3 className="font-semibold text-[#0B1A2F] leading-tight">{a.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{a.summary}</p>
                </div>
              </div>
              <Accordion type="single" collapsible value={openId === aid ? "more" : ""} onValueChange={(v) => setOpenId(v ? aid : "")} className="px-5 pb-2">
                <AccordionItem value="more" className="border-none">
                  <AccordionTrigger className="text-sm text-[#2EA3FD] hover:no-underline py-2" data-testid={`help-expand-${aid}`}>Read more</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pb-2">
                      {(a.body || []).map((s, i) => (
                        <div key={i}>
                          <p className="text-sm font-semibold text-[#0B1A2F]">{s.h}</p>
                          <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{s.p}</p>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          ); })}
        </div>
      )}

      <div className="bg-[#F5FAFF] border border-sky-100 rounded-xl p-5 text-sm text-slate-600 flex items-start gap-3" data-testid="help-footer">
        <LifeBuoy size={18} className="text-[#2EA3FD] shrink-0 mt-0.5" />
        <p>Still stuck? Use the <strong>?</strong> button in the top bar for tips on the page you're on, or contact the People team — we're happy to help.</p>
      </div>
    </div>
  );
}
